/**
 * Client-side catalog search expansions.
 *
 * Product nicknames (cdn, br540, nine pm) and bare-name canonical maps
 * (bleu, homme intense) are shared with fragrance.search_expand_aliases_v1.
 * SKU typos live in SEARCH_TYPO_ALIASES (search_expand_typos_v1). Do not map
 * real homophones (savage → sauvage); that hides Dua Savage Water.
 *
 * Do not reuse searchAbbrev.js here: that map is for collection/house browse
 * and maps `cdn` → Creed, which would hide Club de Nuit (Armaf).
 */

import { normalizeCatalogSearchText } from './catalogSearchConfidence.js'

const WHOLE_QUERY_ALIASES = {
  cdnim: 'club de nuit intense man',
  cdniman: 'club de nuit intense man',
  'cdn intense': 'club de nuit intense',
  'cdn intense man': 'club de nuit intense man',
  cdn: 'club de nuit',
  br540: 'baccarat rouge 540',
  'br 540': 'baccarat rouge 540',
  'br-540': 'baccarat rouge 540',
  baccarat: 'baccarat rouge 540',
  'nine pm': '9pm',
  ninepm: '9pm',
  'nine p.m': '9pm',
  '9 pm': '9pm',
  // FOS-SEARCH-BARE-NAME-001: canonical identity for prefix / near-exact
  // collisions. Shared with fragrance.search_expand_aliases_v1.
  bleu: 'bleu de chanel',
  'homme intense': 'dior homme intense',
};

const TOKEN_ALIASES = {
  jpg: 'jean paul gaultier',
  ysl: 'yves saint laurent',
  mfk: 'maison francis kurkdjian',
  pdm: 'parfums de marly',
  tf: 'tom ford',
  cdl: 'creed',
  adg: 'acqua di gio',
  mfg: 'maison francis kurkdjian',
  br540: 'baccarat rouge 540',
  cdnim: 'club de nuit intense man',
  cdn: 'club de nuit',
  cdni: 'club de nuit intense',
  ninepm: '9pm',
};

// Policy (a) for Muse / Aura, widened by FOS-SEARCH-BARE-NAME-AMBIG-002:
// when the query is exactly a name that collides across houses, cap EVERY
// result below the FE 0.9 miss threshold — not only exact-name equals.
// Contaminants (Muse Of Strawberry) must not look like a trustworthy lock.
// Hunt houses stay visible. House-qualified queries (ysl muse, rayhaan aura,
// Khadlaj Muse) are not capped. Not catalog-wide — khamrah stays 0.99.
export const BARE_NAME_COLLISIONS = ['muse', 'aura'];
export const BARE_NAME_COLLISION_CAP = 0.89;
export const BARE_NAME_COLLISION_REASON = 'ambiguous_bare_name';

// FOS-IDENTITY-HOUSE-LABEL-001: when the typed key equals a house present
// in the result set (norm), prefer those rows and cap name-only hits from
// other houses below the FE 0.9 lock. Defense-in-depth for a stale RPC;
// the SQL resolver is the source of truth and can fill a missing house.
export const HOUSE_QUERY_FOREIGN_CAP = 0.89;
export const HOUSE_QUERY_FOREIGN_REASON = 'house_query_foreign_name';

// FOS-COLLECTION-ENTRY-TRUST-TRAP-001: mapped lifestyle bottle intents.
// Exact preferred house+name only, or empty. Lookalikes are dropped, not
// shown at 0.89. Shared with fragrance.search_canonical_intent_v1.
export const CANONICAL_INTENTS = {
  salt: { prefHouse: 'ellis brooklyn', nameKey: 'salt' },
  'ellis salt': { prefHouse: 'ellis brooklyn', nameKey: 'salt' },
  'ellis brooklyn salt': { prefHouse: 'ellis brooklyn', nameKey: 'salt' },
  'madagascar vanilla': { prefHouse: 'nest', nameKey: 'madagascar vanilla' },
  'nest madagascar vanilla': { prefHouse: 'nest', nameKey: 'madagascar vanilla' },
  hwyl: { prefHouse: 'aesop', nameKey: 'hwyl' },
  'aesop hwyl': { prefHouse: 'aesop', nameKey: 'hwyl' },
  crumb: { prefHouse: 'snif', nameKey: 'crumb' },
  'snif crumb': { prefHouse: 'snif', nameKey: 'crumb' },
  daisy: { prefHouse: 'marc jacobs', nameKey: 'daisy' },
  'marc jacobs daisy': { prefHouse: 'marc jacobs', nameKey: 'daisy' },
};

// Shared with fragrance.search_expand_typos_v1. Token-level only: never map
// `savage` to `sauvage`. Adjacent-transposition / missing-letter typos of
// major SKUs that would otherwise lose to a nearer homophone clone.
export const SEARCH_TYPO_ALIASES = {
  savauge: 'sauvage',
  savuage: 'sauvage',
  suavage: 'sauvage',
  sauvge: 'sauvage',
  bacarrat: 'baccarat',
};

function collapseWs(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalizeKey(value) {
  return collapseWs(value)
    .toLowerCase()
    .replace(/[.’']/g, '')
    .replace(/[-_/]+/g, ' ');
}

export function expandSearchQuery(rawQuery) {
  const original = collapseWs(rawQuery);
  if (!original) return '';

  const key = normalizeKey(original);
  if (WHOLE_QUERY_ALIASES[key]) return WHOLE_QUERY_ALIASES[key];

  const tokens = key.split(' ').filter(Boolean);
  const expanded = tokens.map((token) => (
    TOKEN_ALIASES[token] || SEARCH_TYPO_ALIASES[token] || token
  ));
  return collapseWs(expanded.join(' '));
}

export function queryHasSauvageTypo(rawQuery) {
  const key = normalizeKey(rawQuery);
  if (SEARCH_TYPO_ALIASES[key] === 'sauvage') return true;
  return key.split(' ').some((token) => SEARCH_TYPO_ALIASES[token] === 'sauvage');
}

function haystack(row) {
  return `${row?.name || ''} ${row?.brand || ''} ${row?.house || ''}`.toLowerCase();
}

function preferHouse(rows, houseNeedle) {
  const needle = String(houseNeedle || '').toLowerCase();
  const hits = [];
  const rest = [];
  for (const row of rows) {
    const house = String(row.brand || row.house || '').toLowerCase();
    if (house.includes(needle)) hits.push(row);
    else rest.push(row);
  }
  return hits.length ? [...hits, ...rest] : rows;
}

function preferNameIncludes(rows, fragment) {
  const needle = String(fragment || '').toLowerCase();
  const hits = [];
  const rest = [];
  for (const row of rows) {
    if (String(row.name || '').toLowerCase().includes(needle)) hits.push(row);
    else rest.push(row);
  }
  return hits.length ? [...hits, ...rest] : rows;
}

function preferExactNinePm(rows) {
  const exact = [];
  const rest = [];
  for (const row of rows) {
    const name = String(row.name || '').replace(/\s+/g, ' ').trim();
    if (/^9\s*pm$/i.test(name) || /^9pm$/i.test(name)) exact.push(row);
    else rest.push(row);
  }
  return exact.length ? [...exact, ...rest] : rows;
}

function penalizeHouses(rows, houseNeedles) {
  const needles = houseNeedles.map((h) => h.toLowerCase());
  const keep = [];
  const demote = [];
  for (const row of rows) {
    const house = String(row.brand || row.house || '').toLowerCase();
    if (needles.some((n) => house.includes(n))) demote.push(row);
    else keep.push(row);
  }
  return [...keep, ...demote];
}

function byMatchConfidence(rows) {
  return [...rows].sort((a, b) => {
    const ca = Number(a.match_confidence);
    const cb = Number(b.match_confidence);
    if (Number.isFinite(cb) && Number.isFinite(ca) && cb !== ca) return cb - ca;
    return 0;
  });
}

function byRankDesc(rows) {
  return [...rows].sort((a, b) => (Number(b.rank) || 0) - (Number(a.rank) || 0));
}

export function applyBareNameCollisionPolicy(rawQuery, rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const key = normalizeKey(rawQuery);
  if (!BARE_NAME_COLLISIONS.includes(key)) return rows;
  return rows.map((row) => {
    const conf = Number(row.match_confidence);
    if (Number.isFinite(conf) && conf < BARE_NAME_COLLISION_CAP) return row;
    return {
      ...row,
      match_confidence: BARE_NAME_COLLISION_CAP,
      match_reason: BARE_NAME_COLLISION_REASON,
    };
  });
}

function lookupCanonicalIntent(rawQuery) {
  return CANONICAL_INTENTS[normalizeCatalogSearchText(rawQuery)] || null;
}

function rowMatchesCanonicalIntent(row, intent) {
  const house = normalizeCatalogSearchText(row?.house || row?.brand);
  const name = normalizeCatalogSearchText(row?.name || row?.line_label || row?.line_key);
  if (house !== intent.prefHouse || !name) return false;
  return name === intent.nameKey;
}

export function applyCanonicalIntentPolicy(rawQuery, rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const intent = lookupCanonicalIntent(rawQuery);
  if (!intent) return rows;
  return rows.filter((row) => rowMatchesCanonicalIntent(row, intent));
}

export function applyCanonicalIntentLines(rawQuery, lines) {
  if (!Array.isArray(lines) || lines.length === 0) return lines || [];
  const intent = lookupCanonicalIntent(rawQuery);
  if (!intent) return lines;
  const preferred = lines.filter((line) => rowMatchesCanonicalIntent(line, intent));
  return preferred.length ? preferred : [];
}

export function applyHouseLabelPreferPolicy(rawQuery, rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const keys = new Set(
    [normalizeCatalogSearchText(rawQuery), normalizeCatalogSearchText(expandSearchQuery(rawQuery))]
      .filter(Boolean),
  );
  const houseOf = (row) => normalizeCatalogSearchText(row?.house || row?.brand);
  const isHouseQuery = rows.some((row) => keys.has(houseOf(row)));
  if (!isHouseQuery) return rows;

  const preferred = [];
  const foreign = [];
  for (const row of rows) {
    if (keys.has(houseOf(row))) {
      preferred.push({
        ...row,
        rank: (Number(row.rank) || 0) + 1,
      });
      continue;
    }
    const conf = Number(row.match_confidence);
    if (Number.isFinite(conf) && conf > HOUSE_QUERY_FOREIGN_CAP) {
      foreign.push({
        ...row,
        match_confidence: HOUSE_QUERY_FOREIGN_CAP,
        match_reason: HOUSE_QUERY_FOREIGN_REASON,
      });
    } else {
      foreign.push(row);
    }
  }
  return [...preferred, ...foreign];
}

function isSavageHomophoneName(name) {
  const n = String(name || '').toLowerCase();
  return n.includes('savage') && !n.includes('sauvage');
}

function isDiorSauvageRow(row) {
  const house = String(row?.brand || row?.house || '').toLowerCase();
  const name = String(row?.name || '').toLowerCase();
  return house.includes('dior') && name.includes('sauvage');
}

// Parent prod findability_v1('savauge') ORDER BY rank DESC: Dua Savage Water
// 0.3125 / 0.91 beats Dior Sauvage 0.230769 / 0.91. Equal conf, rank decides.
// Rewrite the rank score so a later rank-desc sort cannot restore the clone.
function preferSauvageOnTypo(rows) {
  return byRankDesc(rows.map((row) => {
    if (isDiorSauvageRow(row)) {
      return { ...row, rank: Math.max(Number(row.rank) || 0, 1) };
    }
    if (isSavageHomophoneName(row.name)) {
      return { ...row, rank: Math.min(Number(row.rank) || 0, 0.2) };
    }
    return row;
  }));
}

/**
 * Re-order RPC rows for known shorthand / concentration misses.
 * Does not drop rows; only moves intended SKUs to the front.
 */
export function rerankCatalogResults(rawQuery, rows) {
  if (!Array.isArray(rows) || rows.length < 2) return rows || [];
  const key = normalizeKey(rawQuery);
  const expanded = normalizeKey(expandSearchQuery(rawQuery));
  let next = [...rows];

  if (key === 'jpg elixir' || expanded.includes('elixir')) {
    next = preferNameIncludes(next, 'elixir');
    next = byMatchConfidence(next);
  }

  if (
    key === 'br540' ||
    key === 'br 540' ||
    key === 'br-540' ||
    expanded.includes('baccarat rouge 540') ||
    expanded === 'baccarat' ||
    SEARCH_TYPO_ALIASES[key] === 'baccarat'
  ) {
    next = penalizeHouses(next, ['oil perfumery', 'dupe', 'inspired']);
    next = preferHouse(next, 'maison francis kurkdjian');
    next = preferNameIncludes(next, 'baccarat rouge');
  }

  if (queryHasSauvageTypo(rawQuery)) {
    next = preferSauvageOnTypo(next);
    next = preferHouse(next, 'dior');
    next = preferNameIncludes(next, 'sauvage');
  }

  if (key === 'bleu' || expanded === 'bleu de chanel') {
    next = preferHouse(next, 'chanel');
    next = preferNameIncludes(next, 'bleu de chanel');
  }

  if (key === 'homme intense' || expanded === 'dior homme intense') {
    next = preferHouse(next, 'dior');
    next = preferNameIncludes(next, 'homme intense');
  }

  if (
    key === 'cdn'
    || key === 'cdnim'
    || key === 'cdni'
    || key.startsWith('cdni ')
    || expanded.includes('club de nuit')
  ) {
    next = preferHouse(next, 'armaf');
    next = preferNameIncludes(next, 'club de nuit');
  }

  if (
    key === 'nine pm' ||
    key === 'ninepm' ||
    key === '9 pm' ||
    key === '9pm' ||
    expanded === '9pm'
  ) {
    next = preferHouse(next, 'afnan');
    next = preferExactNinePm(next);
    next = next.filter((row) => !/night out/i.test(haystack(row))).concat(
      next.filter((row) => /night out/i.test(haystack(row)))
    );
  }

  return next;
}

export const SEARCH_QUERY_WHOLE_ALIASES = WHOLE_QUERY_ALIASES;
export const SEARCH_QUERY_TOKEN_ALIASES = TOKEN_ALIASES;
